import { useId, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { PreparedCardAction } from '../../../lib/card-actions';
import { Modal } from '../../Modal';
import { SymphonyMark } from '../../SymphonyMark';
import { Button, Switch, Typography } from '../ui';

/**
 * The confirm for a card's direct write. It is drawn by the app, outside the frame, from what the harness
 * read from Wix, and it looks like nothing a widget's `sym.confirm` can produce (that one is a title and a
 * message): the Symphony mark leads the title, a provenance line sits under it, the record's facts are a
 * list, and what Wix may tell the customer, or that the write cannot be undone, is the footnote. A card
 * script can pick which record the dialog names, so the owner has to be able to tell the app is speaking.
 *
 * When prepare `offersCustomerNotification` (a cancellation), a "Notify the customer" switch sits under the
 * lines, on every time the dialog opens. Off hides `customerNotice`, since Wix will then tell the customer
 * nothing, and the answer carries `notifyCustomer: false` for execute to send `skipCustomerNotification`.
 * Same contract on iOS and Android.
 */
export function CardActionDialog({
  prepared,
  undo,
  onAnswer,
}: {
  prepared: PreparedCardAction;
  undo: boolean;
  onAnswer: (ok: boolean, options: { notifyCustomer: boolean }) => void;
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const titleId = useId();
  // Mounted fresh for each dialog (the host keys it by token), so this is ON every time one opens.
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const offersNotify = prepared.offersCustomerNotification;
  // `cancel` is the Cancel button; `close` is the ✕, Escape or a scrim click, as the app's own confirm reports them.
  const answer = (action: 'confirm' | 'cancel' | 'close') => {
    logModalActionBi({ page_name: 'dashboard', modal_name: 'card_action', action });
    onAnswer(action === 'confirm', { notifyCustomer: !offersNotify || notifyCustomer });
  };
  const showNotice = !offersNotify || notifyCustomer;
  const notes = [showNotice ? prepared.customerNotice : '', !undo && !prepared.reversible ? t('cardAction.cannotUndo') : ''].filter(Boolean);
  return (
    <Modal
      open
      onClose={() => answer('close')}
      width={440}
      maxHeightPx={650}
      scrollBody
      labelledBy={titleId}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <SymphonyMark size={24} />
          {prepared.title}
        </span>
      }
      description={t('cardAction.readFromWix')}
      footnote={notes.length > 0 ? notes.map((note) => <div key={note}>{note}</div>) : undefined}
      footer={
        <>
          <Button variant="outlined" onClick={() => answer('cancel')}>
            {t('common.cancel')}
          </Button>
          <Button color={prepared.destructive ? 'destructive' : 'accent'} onClick={() => answer('confirm')}>
            {t('common.confirm')}
          </Button>
        </>
      }
    >
      {prepared.lines.length > 0 && (
        <ul style={{ margin: 0, paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {prepared.lines.map((line, i) => (
            <li key={i}>
              <Typography variant="p2" component="span">
                {line}
              </Typography>
            </li>
          ))}
        </ul>
      )}
      {offersNotify && (
        <div style={{ marginTop: prepared.lines.length > 0 ? 16 : 0 }}>
          <Switch checked={notifyCustomer} onChange={setNotifyCustomer} label={t('cardAction.notifyCustomer')} />
        </div>
      )}
    </Modal>
  );
}
